import React, { Component } from 'react';
import { connect } from 'react-redux';
import Select from 'react-select';
import { ErrorMessage } from '../../common';
import actions from '../../../actions';
import { getErrorMessages } from '../../../utils';
import { fetchSettings } from '../../../epics/api';
import { TDispatch, TRootState } from '../../../reducers';
import { TFreelancerData, TPageNotification } from '../../../types';
import { NotificationType } from '../../../consts';
import './SendNotification.style.scss';

type NotificationTypeValue = typeof NotificationType[keyof typeof NotificationType];

type NotificationOption = {
    value: NotificationTypeValue;
    label: string;
};

type SendNotificationProps = {
    freelancers: TFreelancerData[];
    notification: TPageNotification;
    sendNotification: (
        freelancers: number[],
        titleText: string,
        messageText: string,
        notificationType: NotificationTypeValue,
        attachments: File[],
        response: any,
        error: any,
        isUnallocatedFreelancersRoute?: boolean,
    ) => void;
    onActionSuccess?: () => void;
    closeModal: () => void;
    isUnallocatedFreelancersRoute?: boolean;
};

type SendNotificationState = {
    titleText: string;
    messageText: string;
    notificationType: NotificationTypeValue;
    sending: boolean;
    error: string;
    maxMessageLength: number;
    attachments: File[];
    attachmentSettings: {
        maxAttachments: number;
        maxAttachmentSize: number;
        allowedExtensions: string[];
    };
};

class SendNotificationContainer extends Component<
    SendNotificationProps,
    SendNotificationState
> {
    private notificationTypeOptions: NotificationOption[] = [
        {
            value: NotificationType.EMAIL_AND_PUSH,
            label: 'Email + Push Notification',
        },
        {
            value: NotificationType.EMAIL_ONLY,
            label: 'Email Only',
        },
        {
            value: NotificationType.PUSH_ONLY,
            label: 'Push Notification',
        },
    ];

    constructor(props: SendNotificationProps) {
        super(props);
        this.state = {
            titleText: '',
            messageText: '',
            notificationType: NotificationType.EMAIL_AND_PUSH,
            sending: false,
            error: null,
            maxMessageLength: 160, // Default fallback value
            attachments: [],
            attachmentSettings: {
                maxAttachments: 1,
                maxAttachmentSize: 10485760, // 10MB in bytes
                allowedExtensions: ['pdf'],
            },
        };
        this.sendMessage = this.sendMessage.bind(this);
    }

    async componentDidMount() {
        try {
            const response = await fetchSettings();
            const notificationSettings = response.data.content.notification;
            this.setState({
                maxMessageLength: notificationSettings.pushnotification_max_message_length,
                attachmentSettings: {
                    maxAttachments: notificationSettings.attachment.max_attachments,
                    maxAttachmentSize: notificationSettings.attachment.max_attachment_size,
                    allowedExtensions: notificationSettings.attachment.allowed_extensions,
                },
            });
        } catch (error) {
            // If settings fetch fails, keep the default value and continue
            console.error('Failed to fetch settings:', error);
        }
    }

    handleTitleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ titleText: event.target.value });
    };

    handleMessageTextChange = (
        event: React.ChangeEvent<HTMLTextAreaElement>,
    ) => {
        this.setState({ messageText: event.target.value });
    };

    handleNotificationTypeChange = (selectedOption: NotificationOption) => {
        this.setState({
            notificationType: selectedOption.value,
        });
    };

    handleAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        const { attachments, attachmentSettings } = this.state;
        
        // Validate file count
        if (attachments.length + files.length > attachmentSettings.maxAttachments) {
            this.setState({
                error: `Maximum ${attachmentSettings.maxAttachments} attachments allowed. You are trying to add ${files.length} more files, but you already have ${attachments.length} files.`,
            });
            return;
        }

        // Validate each file
        const validFiles: File[] = [];
        const errors: string[] = [];

        files.forEach(file => {
            // Check file size
            if (file.size > attachmentSettings.maxAttachmentSize) {
                errors.push(`${file.name}: File size exceeds ${this.formatFileSize(attachmentSettings.maxAttachmentSize)}`);
                return;
            }

            // Check file extension
            const fileExtension = file.name.split('.').pop()?.toLowerCase();
            if (!fileExtension || !attachmentSettings.allowedExtensions.includes(fileExtension)) {
                errors.push(`${file.name}: File type not allowed. Allowed types: ${attachmentSettings.allowedExtensions.join(', ')}`);
                return;
            }

            validFiles.push(file);
        });

        if (errors.length > 0) {
            this.setState({
                error: errors.join('\n'),
            });
            return;
        }

        this.setState({
            attachments: [...attachments, ...validFiles],
            error: null,
        });
    };

    removeAttachment = (index: number) => {
        const { attachments } = this.state;
        this.setState({
            attachments: attachments.filter((_, i) => i !== index),
        });
    };

    formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
    };

    sendMessage = () => {
        this.setState({ sending: true });
        this.props.sendNotification(
            this.props.freelancers.map(fl => fl.id),
            this.state.titleText,
            this.state.messageText,
            this.state.notificationType,
            this.state.attachments,
            (response: {
                // eslint-disable-next-line camelcase
                data: { content: { notification_id: number[]; sent: number } };
            }) => {
                let successMessage: string;
                if (response.data.content.sent > 1) {
                    successMessage = `Successfully sent notification to ${response.data.content.sent} workers`;
                } else if (this.props.freelancers.length === 1) {
                    successMessage = `Successfully sent notification to ${this.props.freelancers[0].full_name}`;
                } else {
                    successMessage = 'Successfully sent notification';
                }
                this.props.notification.success('Success', successMessage);
                this.props.closeModal();
                this.props.onActionSuccess();
            },
            (error: { response: { data: { meta: { detail: any } } } }) => {
                if (error && error.response) {
                    this.setState({ error: getErrorMessages(error) });
                }
                this.setState({
                    sending: false,
                });
            },
            this.props.isUnallocatedFreelancersRoute,
        );
    };

    render() {
        const countOfSelected = this.props.freelancers.length;
        const selectedNotificationOption = this.notificationTypeOptions.find(
            option => option.value === this.state.notificationType
        );

        return (
            <div>
                <div className="modal-dialog notification-message-container">
                    <div className="popup-header">
                        <div
                            className="popup-header-dismiss"
                            onClick={this.props.closeModal}
                        >
                            <i className="icon icon-close" />
                        </div>
                        <h1>Send Notification to worker</h1>
                    </div>
                    <form className="notification-form">
                        <div className="form-group">
                            <label htmlFor="notification-type">Notification Type</label>
                            <Select
                                name="notification-type"
                                value={selectedNotificationOption}
                                onChange={this.handleNotificationTypeChange}
                                options={this.notificationTypeOptions}
                                isDisabled={this.state.sending}
                                isClearable={false}
                                isSearchable={false}
                            />
                        </div>
                        {this.state.notificationType !==
                            NotificationType.PUSH_ONLY && (
                                <div className="form-group">
                                    <label htmlFor="notification-title">
                                        Email Subject
                                    </label>
                                    <input
                                        id="notification-title"
                                        type="text"
                                        value={this.state.titleText}
                                        onChange={this.handleTitleTextChange}
                                        placeholder="Type in email subject here"
                                        maxLength={100}
                                        className="form-control"
                                    />
                                </div>
                            )}
                        <div className="form-group">
                            <label htmlFor="notification-message">
                                Message ({this.state.messageText.length}
                                {this.state.notificationType === NotificationType.PUSH_ONLY &&
                                  (`/${this.state.maxMessageLength}`)})
                            </label>
                            <textarea
                                id="notification-message"
                                name="message"
                                value={this.state.messageText}
                                onChange={this.handleMessageTextChange}
                                placeholder="Type in message here"
                                maxLength={this.state.notificationType !== NotificationType.PUSH_ONLY ? 1000 :
                                  this.state.maxMessageLength}
                                rows={15}
                                className="form-control"
                            />
                            {this.state.messageText.length > this.state.maxMessageLength &&
                              this.state.notificationType === NotificationType.EMAIL_AND_PUSH && (
                                <div className="character-warning">
                                    <i className="icon icon-warning" />
                                    Warning: Message too long ({this.state.messageText.length}/{this.state.maxMessageLength} characters).
                                    Push notifications will be shortened, but recipients will receive the complete message by email.
                                </div>
                            )}
                        </div>
                        <div className="form-group">
                            <label htmlFor="attachments">
                                Attachments ({this.state.attachments.length}/{this.state.attachmentSettings.maxAttachments})
                            </label>
                            <div className={`attachment-upload-area ${this.state.attachments.length >= this.state.attachmentSettings.maxAttachments ? 'disabled' : ''}`}>
                                <input
                                    id="attachments"
                                    type="file"
                                    multiple={true}
                                    onChange={this.handleAttachmentChange}
                                    accept={this.state.attachmentSettings.allowedExtensions.map(ext => `.${ext}`).join(',')}
                                    className="file-input"
                                    disabled={this.state.sending || this.state.attachments.length >= this.state.attachmentSettings.maxAttachments}
                                />
                                <div className="upload-info">
                                    {this.state.attachments.length >= this.state.attachmentSettings.maxAttachments ? (
                                        <p className="max-reached">Maximum number of attachments reached</p>
                                    ) : (
                                        <p>Click to select files or drag and drop</p>
                                    )}
                                    <p className="file-constraints">
                                        Max {this.state.attachmentSettings.maxAttachments} files, 
                                        {this.formatFileSize(this.state.attachmentSettings.maxAttachmentSize)} each
                                    </p>
                                    <p className="allowed-types">
                                        Allowed types: {this.state.attachmentSettings.allowedExtensions.join(', ')}
                                    </p>
                                </div>
                            </div>
                            {this.state.attachments.length > 0 && (
                                <div className="attachment-list">
                                    {this.state.attachments.map((file, index) => (
                                        <div key={`attachment-${file.name}-${file.size}-${file.lastModified}`} className="attachment-item">
                                            <div className="file-info">
                                                <i className="icon icon-file" />
                                                <span className="file-name">{file.name}</span>
                                                <span className="file-size">({this.formatFileSize(file.size)})</span>
                                            </div>
                                            <button
                                                type="button"
                                                className="remove-attachment"
                                                onClick={() => this.removeAttachment(index)}
                                                disabled={this.state.sending}
                                            >
                                                <i className="icon icon-close" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </form>
                    <div className="modal-bottom-bar">
                        <button
                            className="table-action-button"
                            type="button"
                            onClick={this.sendMessage}
                            disabled={
                                (this.state.notificationType !==
                                    NotificationType.PUSH_ONLY &&
                                    !this.state.titleText) ||
                                !this.state.messageText ||
                                this.state.sending
                            }
                        >
                            {(() => {
                                if (this.state.sending) {
                                    return 'Sending...';
                                }
                                if (countOfSelected > 1) {
                                    return `Send Message to ${countOfSelected} workers`;
                                }
                                return 'Send Message';
                            })()}
                        </button>
                    </div>
                </div>
                {this.state.error ? (
                    <ErrorMessage message={this.state.error} />
                ) : false}
            </div>
        );
    }
}

const mapStateToProps = (state: TRootState) => ({
    user: state.user,
});

const mapDispatchToProps = (dispatch: TDispatch) => ({
    sendNotification: (
        recipients: number[],
        title: string,
        message: string,
        notificationType: NotificationTypeValue,
        attachments: File[],
        successCallback: any,
        errorCallback: any,
        isUnallocatedFreelancersRoute: boolean,
    ) =>
        dispatch(
            actions.sendNotification({
                recipients,
                title,
                message,
                notificationType,
                attachments,
                successCallback,
                errorCallback,
                isUnallocatedFreelancersRoute,
            }),
        ),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(SendNotificationContainer);
